import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import { getStoryDisplayTitle } from "../storyDisplayUtils";

function readTileMode(row) {
    const raw = row?.details?.tileMode ?? row?.tileMode;
    const mode = typeof raw === "string" ? raw.trim().toLowerCase() : "";
    if (mode === "image" || mode === "numbers") return mode;
    return "";
}

function mapPlayRow(row) {
    if (!row || typeof row !== "object") return null;

    const story = row.story ?? null;
    const sliding = row.sliding ?? null;
    const puzzleTitle = getStoryDisplayTitle(story) || story?.id || "—";
    const isComplete = row.isCompleted === true;
    const gridSize = sliding?.gridSize ?? null;
    const difficulty = sliding?.difficultyLevel ?? sliding?.difficulty ?? null;

    return {
        id: row.id,
        name: `${row.cloudUser?.firstName ?? ""} ${row.cloudUser?.lastName ?? ""}`.trim() || "—",
        email: row.cloudUser?.email ?? "—",
        puzzleTitle,
        gridSize,
        difficulty,
        tileMode: readTileMode(row),
        startedAt: UtilService.formatDate(row.startedAt),
        finishedAt: UtilService.formatDate(row.finishedAt),
        isCompleted: isComplete,
        status: isComplete ? "Completed" : "In Progress",
        attemptsCount: row.attemptsCount ?? 0,
        currentLayout: row.currentLayout ?? null,
        slidingId: sliding?.id ?? null,
        imageUrl: story?.imageUrl ?? story?.squareImageUrl ?? null,
        story,
        sliding,
        storySliding: row.storySliding ?? null,
        cloudUser: row.cloudUser ?? null,
    };
}

const AdminSlidingPlayService = {
    mapPlayRow,

    requestPuzzlePlay: async (
        slidingId,
        keyword,
        pageModel,
        sort,
        setRows,
        setRowCount,
        isCompleted,
        onlyStarted = false,
    ) => {
        const params = {
            page: pageModel.page,
            pageSize: pageModel.pageSize,
            sort: sort.map((s) => `${s.field}:${s.sort}`).join(","),
        };

        if (slidingId) {
            params.slidingId = slidingId;
        }

        if (keyword) {
            params.keyword = keyword;
        }

        if (isCompleted !== null) {
            params.isCompleted = isCompleted;
        }

        if (onlyStarted === true) {
            params.onlyStarted = true;
        }

        const result = await ApiService.getData("/v1/puzzle/sliding-play", params);
        const mapped = (result?.content ?? []).map(mapPlayRow).filter(Boolean);
        setRows(mapped);
        setRowCount(result?.page?.totalElements ?? result?.totalElements ?? 0);
    },

    listPlays: async ({ storyId, page = 0, pageSize = 8, sort = "startedAt:desc", isCompleted } = {}) => {
        const params = { page, pageSize, sort };
        if (storyId) {
            params.storyId = storyId;
        }
        if (isCompleted !== null && isCompleted !== undefined) {
            params.isCompleted = isCompleted;
        }
        const result = await ApiService.getData("/v1/puzzle/sliding-play", params);
        return {
            rows: (result?.content ?? []).map(mapPlayRow).filter(Boolean),
            total: result?.page?.totalElements ?? result?.totalElements ?? 0,
        };
    },

    requestPlayDetail: async (playId) => {
        if (!playId) {
            throw new Error("Play id is required");
        }

        try {
            const row = await ApiService.getData(`/v1/puzzle/sliding-play/${encodeURIComponent(playId)}`);
            const mapped = mapPlayRow(row);
            if (mapped) return mapped;
        } catch (error) {
            Logger.warn("GET /v1/puzzle/sliding-play/:id failed, falling back to list lookup", error);
        }

        const result = await ApiService.getData("/v1/puzzle/sliding-play", {
            page: 0,
            pageSize: 200,
            sort: "startedAt:desc",
        });
        const found = (result?.content ?? []).find((row) => row.id === playId);
        if (!found) {
            throw new Error("Play session not found");
        }
        return mapPlayRow(found);
    },

    deleteSlidingPlay: async (slidingPlayIds) => {
        try {
            const ids = Array.isArray(slidingPlayIds) ? slidingPlayIds : [slidingPlayIds];
            await ApiService.deleteData("/v1/puzzle/sliding-play", { slidingPlayIds: ids });
            return true;
        } catch (error) {
            Logger.error("Error deleting sliding play:", error);
            throw error;
        }
    },
};

export default AdminSlidingPlayService;
